import type { Media, Shop } from '@prisma/client'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useFetcher } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import type { AmendableAttachmentType } from '~/contexts/MediaManagerContext'
import { makeShopOwnPath } from '~/helpers/routes'

export interface AttachmentInfo {
  id?: string
  media: Media
  mediaSrc: string
  disposition: string
}

interface UseAttachmentManagerOptions {
  shop: Shop
  maxSelected?: number
  targetType?: string
  targetId?: string
  initialAttachments?: AmendableAttachmentType[]
  onComplete?: (attachments: AmendableAttachmentType[]) => void
  onChange?: (attachments: AmendableAttachmentType[]) => void
}

function getAttachmentKey(attachments?: AmendableAttachmentType[]): string {
  if (!attachments || attachments.length === 0) return ''
  return attachments
    .map((a) => `${a.id ?? a.media.id}:${a.disposition}`)
    .sort()
    .join(',')
}

export function useAttachmentManager({
  shop,
  maxSelected,
  targetType,
  targetId,
  initialAttachments,
  onComplete,
  onChange,
}: UseAttachmentManagerOptions) {
  const updateFetcher = useFetcher({ key: 'update' })

  const [attachmentItems, setAttachmentItems] = useState<AttachmentInfo[]>([])
  const [initialAttachmentItems, setInitialAttachmentItems] = useState<
    AttachmentInfo[]
  >([])
  const [placeholderItems, setPlaceholderItems] = useState<string[]>(
    maxSelected
      ? Array(maxSelected)
          .fill(null)
          .map(() => uuidv4())
      : []
  )

  const [itemsToAdd, setItemsToAdd] = useState<AttachmentInfo[]>()
  const [itemsToUpdate, setItemsToUpdate] = useState<AttachmentInfo[]>()
  const [itemsToArchive, setItemsToArchive] = useState<string[]>()
  const [updating, setUpdating] = useState<boolean>(false)
  const [isAttachmentsDirty, setIsAttachmentsDirty] = useState<boolean>(false)

  // Track whether we have a submission in flight so we don't dequeue
  // until the request actually completes (fetcher returns to idle).
  const hasSubmittedRef = useRef(false)

  const isNewSelection = useMemo(
    () => initialAttachmentItems.length === 0,
    [initialAttachmentItems.length]
  )

  // Stable key derived from attachment content AND target identity.
  // Prevents the init effect from re-firing on every render when the
  // parent passes a new array reference with the same data, while
  // ensuring it DOES fire when switching between different contexts
  // (e.g., hero slider → variant) that both start with empty attachments.
  const initialAttachmentKey = useMemo(
    () => `${targetType}:${targetId}:${getAttachmentKey(initialAttachments)}`,
    [initialAttachments, targetType, targetId]
  )

  // Load initial attachments — only when the *content* actually changes
  // and we're not in the middle of committing updates.
  useEffect(() => {
    if (updating) return

    if (!initialAttachments) {
      setAttachmentItems([])
      setInitialAttachmentItems([])
      setIsAttachmentsDirty(false)
      return
    }

    const updatedAttachmentItems: AttachmentInfo[] = initialAttachments.map(
      (att) => ({
        id: att.id,
        media: att.media,
        mediaSrc: att.media.src,
        disposition: String(att.disposition),
      })
    )

    const sorted = updatedAttachmentItems.sort(
      (a, b) => Number(a.disposition) - Number(b.disposition)
    )
    setAttachmentItems(sorted)
    setInitialAttachmentItems(sorted)
    setIsAttachmentsDirty(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAttachmentKey])

  // Update placeholders when attachment count changes
  useEffect(() => {
    if (!maxSelected) return

    const difference =
      maxSelected - attachmentItems.length - placeholderItems.length

    if (difference > 0) {
      const newItems = Array(difference)
        .fill(null)
        .map(() => uuidv4())
      setPlaceholderItems((old) => [...old, ...newItems])
    } else if (difference < 0) {
      setPlaceholderItems((old) => old.slice(0, difference))
    }
  }, [attachmentItems.length, maxSelected, placeholderItems.length])

  // Process attachment updates via fetcher, one request at a time.
  //
  // Key fix: we no longer dequeue (slice) an item from the array at the
  // same time we submit it. Instead we submit and set `hasSubmittedRef`.
  // When the fetcher returns to idle (request completed + revalidation
  // done), we dequeue and let the next render pick up the next item.
  // This ensures the completion check only fires after every request has
  // actually finished.
  useEffect(() => {
    if (!updating || updateFetcher.state !== 'idle') return

    // Fetcher just returned to idle after a submission — dequeue the
    // item that was just processed.
    if (hasSubmittedRef.current) {
      hasSubmittedRef.current = false

      if (itemsToArchive && itemsToArchive.length > 0) {
        setItemsToArchive((old) => old?.slice(1))
        return
      }
      if (itemsToUpdate && itemsToUpdate.length > 0) {
        setItemsToUpdate((old) => old?.slice(1))
        return
      }
      if (itemsToAdd && itemsToAdd.length > 0) {
        setItemsToAdd((old) => old?.slice(1))
        return
      }
    }

    // Submit the next pending item
    if (itemsToArchive?.[0]) {
      hasSubmittedRef.current = true
      updateFetcher.submit(
        { _intent: 'archiveAttachment' },
        {
          method: 'POST',
          action: makeShopOwnPath(shop, `/attachments/${itemsToArchive[0]}`),
        }
      )
      return
    }

    if (itemsToUpdate?.[0]) {
      hasSubmittedRef.current = true
      updateFetcher.submit(
        { disposition: itemsToUpdate[0].disposition },
        {
          method: 'PATCH',
          action: makeShopOwnPath(shop, `/attachments/${itemsToUpdate[0].id}`),
        }
      )
      return
    }

    if (itemsToAdd?.[0]) {
      hasSubmittedRef.current = true
      updateFetcher.submit(
        {
          mediaId: itemsToAdd[0].media.id,
          disposition: itemsToAdd[0].disposition,
          targetType: targetType || null,
          targetId: targetId || '',
        },
        {
          method: 'POST',
          action: makeShopOwnPath(shop, '/attachments'),
        }
      )
    }
  }, [
    updateFetcher,
    updating,
    itemsToArchive,
    itemsToUpdate,
    itemsToAdd,
    shop,
    targetType,
    targetId,
  ])

  // Complete when all updates are done.
  // With the dequeue-on-idle fix above, arrays only reach length 0
  // after every request has completed and revalidated.
  useEffect(() => {
    if (
      itemsToArchive?.length === 0 &&
      itemsToUpdate?.length === 0 &&
      itemsToAdd?.length === 0 &&
      updating &&
      !hasSubmittedRef.current
    ) {
      setUpdating(false)

      const attachments = attachmentItems.map((attInfo) => ({
        id: attInfo.id,
        media: attInfo.media,
        targetId: targetId || undefined,
        targetIdByKey: '',
        targetType: targetType,
        disposition: Number(attInfo.disposition),
      }))

      onComplete?.(attachments)
    }
  }, [
    itemsToArchive,
    itemsToUpdate,
    itemsToAdd,
    updating,
    attachmentItems,
    targetId,
    targetType,
    onComplete,
  ])

  const handleSelect = useCallback(
    (mediaId: string, media: Media, toSelect: boolean) => {
      setIsAttachmentsDirty(true)

      if (!toSelect) {
        setAttachmentItems((old) =>
          old
            .filter((att) => att.media.id !== mediaId)
            .sort((a, b) => Number(a.disposition) - Number(b.disposition))
        )
      } else if (!maxSelected || attachmentItems.length < maxSelected) {
        setAttachmentItems((old) => {
          const newItems = [
            ...old,
            {
              id: undefined,
              media,
              mediaSrc: media.src,
              disposition: String(old.length + 1),
            },
          ]
          return newItems.sort(
            (a, b) => Number(a.disposition) - Number(b.disposition)
          )
        })
      }
    },
    [maxSelected, attachmentItems.length]
  )

  const handleReorder = useCallback((items: AttachmentInfo[]) => {
    setIsAttachmentsDirty(true)
    setAttachmentItems(
      items
        .map((item, index) => ({ ...item, disposition: String(index) }))
        .sort((a, b) => Number(a.disposition) - Number(b.disposition))
    )
  }, [])

  const commitChanges = useCallback(() => {
    const toAdd = attachmentItems.filter((curAtt) => !curAtt.id)
    const toUpdate = attachmentItems.filter((curAtt) => !!curAtt.id)
    const toArchive = initialAttachmentItems
      .filter(
        (initAtt) =>
          !!initAtt.id &&
          !attachmentItems.some((curAtt) => curAtt.id === initAtt.id)
      )
      .map((att) => att.id!)

    hasSubmittedRef.current = false
    setItemsToAdd(toAdd)
    setItemsToUpdate(toUpdate)
    setItemsToArchive(toArchive)
    setUpdating(true)
  }, [attachmentItems, initialAttachmentItems])

  const returnChanges = useCallback(() => {
    const attachments = attachmentItems.map((attInfo) => ({
      id: attInfo.id,
      media: attInfo.media,
      targetId: targetId || undefined,
      targetIdByKey: '',
      targetType: targetType,
      disposition: Number(attInfo.disposition),
    }))

    onChange?.(attachments)
  }, [attachmentItems, targetId, targetType, onChange])

  return {
    attachmentItems,
    placeholderItems,
    isAttachmentsDirty,
    isNewSelection,
    updating,
    handleSelect,
    handleReorder,
    commitChanges,
    returnChanges,
  }
}
